/**
 * Sticky Navigation Web Component
 * A fixed navigation bar for scrolling to anchor sections on the page
 */
class StickyNavigation extends HTMLElement {
  constructor() {
    super();

    // Configuration from data attributes
    this.config = {
      position: 'top',
      visibility: 'always',
      scrollPercentage: 10,
      docked: false,
      activeStyle: 'underline',
      underlineWidth: 'tab',
      buttonFunction: 'scroll_to',
      buttonScrollAnchor: null,
      buttonScrollOffset: 20
    };

    // State
    this.isVisible = false;
    this.activeIndex = -1;
    this.anchorSections = [];
    this.splideInstance = null;
    this.isScrolling = false;
    this.quantity = 1;
    this.activeScrollTicking = false;

    // Scroll end detection state
    this.scrollEndTimeout = null;
    this.scrollTargetPosition = null;
    this.lastScrollCheckPosition = 0;
    this.scrollStableCount = 0;

    // Bound methods
    this.onScroll = this.onScroll.bind(this);
    this.onNavItemClick = this.onNavItemClick.bind(this);
    this.onButtonClick = this.onButtonClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.handleOffersQuantityChange = this.handleOffersQuantityChange.bind(this);
    this.handleMainFormQuantityChange = this.handleMainFormQuantityChange.bind(this);

    // Drawer
    this.drawerOpen = false;
    this.onDrawerEscape = this.onDrawerEscape.bind(this);
  }

  connectedCallback() {
    this.parseConfig();
    this.cacheElements();
    this.setupEventListeners();
    this.setupQuantityListeners();
    this.initializeSplide();
    this.initializeVisibility();

    // Theme editor: close drawer on section re-render
    if (Shopify && Shopify.designMode) {
      this._editorUnload = () => {
        if (this.drawerOpen) this.closeDrawer();
      };
      document.addEventListener('shopify:section:unload', this._editorUnload);
      document.addEventListener('shopify:section:select', this._editorUnload);
    }

    // Small delay to allow DOM to settle before setting up observers
    requestAnimationFrame(() => {
      this.setupSectionObservers();
      this.updateIndicators();
    });
  }

  disconnectedCallback() {
    // Force close drawer and restore body scroll before cleanup
    if (this.drawerOpen) {
      this.drawerOpen = false;
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this.onDrawerEscape);
    }

    // Theme editor cleanup
    if (this._editorUnload) {
      document.removeEventListener('shopify:section:unload', this._editorUnload);
      document.removeEventListener('shopify:section:select', this._editorUnload);
    }

    this.removeEventListeners();
    this.removeQuantityListeners();
    this.destroySectionObservers();
    if (this.scrollEndTimeout) {
      clearTimeout(this.scrollEndTimeout);
    }
    if (this.splideInstance) {
      this.splideInstance.destroy();
    }
  }

  // === INITIALIZATION ===

  parseConfig() {
    this.config.position = this.dataset.position || 'top';
    this.config.visibility = this.dataset.visibility || 'always';
    this.config.scrollPercentage = parseInt(this.dataset.scrollPercentage) || 10;
    this.config.docked = this.dataset.docked === 'true';
    this.config.activeStyle = this.dataset.activeStyle || 'underline';
    this.config.backgroundAnimation = this.dataset.backgroundAnimation || 'slide';
    this.config.activeColorType = this.dataset.activeColorType || 'scheme';
    this.config.underlineWidth = this.dataset.underlineWidth || 'tab';
    this.config.underlineExtraWidth = parseInt(this.dataset.underlineExtraWidth) || 0;
    this.config.buttonFunction = this.dataset.buttonFunction || 'scroll_to';
    this.config.buttonCustomVariantId = this.dataset.buttonCustomVariantId ? parseInt(this.dataset.buttonCustomVariantId) : null;
    this.config.buttonScrollAnchor = this.dataset.buttonScrollAnchor || null;
    this.config.buttonScrollOffset = parseInt(this.dataset.buttonScrollOffset) || 20;
    this.config.buttonSkipCart = this.dataset.buttonSkipCart === 'true';
    const parsedGap = parseInt(this.dataset.mobileMenuGap);
    this.config.mobileMenuGap = Number.isNaN(parsedGap) ? 8 : parsedGap;
    this.config.activeTextColor = this.dataset.activeTextColor || null;
    this.config.mobileDisplay = this.dataset.mobileDisplay || 'slider';
    this.config.drawerPosition = this.dataset.drawerPosition || 'left';
    this.blockId = this.dataset.blockId;
  }

  cacheElements() {
    this.navItems = this.querySelectorAll('[data-nav-item]');
    this.button = this.querySelector('[data-nav-button]');
    this.mobileButton = this.querySelector('[data-nav-button-mobile]');
    this.desktopMenu = this.querySelector('[data-menu-desktop]');
    this.mobileMenu = this.querySelector('[data-menu-mobile]');

    // Active indicator elements
    this.slidingBg = this.querySelector('[data-sliding-bg]');
    this.underline = this.querySelector('[data-underline]');

    // Drawer elements
    this.drawerToggle = this.querySelector('[data-drawer-toggle]');
    this.drawerPanel = this.querySelector('[data-drawer-panel]');
    this.drawerOverlay = this.querySelector('[data-drawer-overlay]');
    this.drawerIconOpen = this.querySelector('[data-drawer-icon-open]');
    this.drawerIconClose = this.querySelector('[data-drawer-icon-close]');
    this.drawerButton = this.querySelector('[data-nav-button-drawer]');
  }

  setupEventListeners() {
    // Scroll listener for visibility
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize, { passive: true });

    // Nav item clicks
    this.navItems.forEach(item => {
      item.addEventListener('click', this.onNavItemClick);
    });

    // Button click
    if (this.button) {
      this.button.addEventListener('click', this.onButtonClick);
    }

    // Mobile button click
    if (this.mobileButton) {
      this.mobileButton.addEventListener('click', this.onButtonClick);
    }

    // Drawer button click
    if (this.drawerButton) {
      this.drawerButton.addEventListener('click', this.onButtonClick);
    }

    // Drawer toggle
    if (this.drawerToggle) {
      this.drawerToggle.addEventListener('click', () => {
        if (this.drawerOpen) {
          this.closeDrawer();
        } else {
          this.openDrawer();
        }
      });
    }
    if (this.drawerOverlay) {
      this.drawerOverlay.addEventListener('click', () => this.closeDrawer());
    }
  }

  removeEventListeners() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onResize);

    if (this.onDockedScroll) {
      window.removeEventListener('scroll', this.onDockedScroll);
    }

    if (this.onDockedResize) {
      window.removeEventListener('resize', this.onDockedResize);
    }

    if (this.dockedPlaceholder && this.dockedPlaceholder.parentNode) {
      this.dockedPlaceholder.parentNode.removeChild(this.dockedPlaceholder);
    }

    this.navItems.forEach(item => {
      item.removeEventListener('click', this.onNavItemClick);
    });

    if (this.button) {
      this.button.removeEventListener('click', this.onButtonClick);
    }

    if (this.mobileButton) {
      this.mobileButton.removeEventListener('click', this.onButtonClick);
    }

    if (this.drawerButton) {
      this.drawerButton.removeEventListener('click', this.onButtonClick);
    }

    document.removeEventListener('keydown', this.onDrawerEscape);
  }

  setupQuantityListeners() {
    // Listen for Offers block quantity changes
    document.addEventListener('offers:quantity-changed', this.handleOffersQuantityChange);

    // Find and listen to main form quantity input
    this.mainQuantityInput = document.querySelector(
      'quantity-input input[name="quantity"], ' +
      '.product-form__quantity input[name="quantity"], ' +
      '.product-form__input input[name="quantity"], ' +
      'product-form input[name="quantity"], ' +
      'form[action*="/cart/add"] input[name="quantity"], ' +
      '.quantity__input[name="quantity"]'
    );

    // Also try to find quantity select (dropdown style)
    if (!this.mainQuantityInput) {
      this.mainQuantityInput = document.querySelector(
        '.quantity--dropdown select[name="quantity"], ' +
        'product-form select[name="quantity"], ' +
        'form[action*="/cart/add"] select[name="quantity"]'
      );
    }

    if (this.mainQuantityInput) {
      this.mainQuantityInput.addEventListener('change', this.handleMainFormQuantityChange);
      // Sync initial quantity from the form
      const initialQty = parseInt(this.mainQuantityInput.value) || 1;
      if (initialQty !== this.quantity) {
        this.quantity = initialQty;
      }
    }

    // Also sync from offers block if it exists (after Alpine initializes)
    setTimeout(() => {
      const selectedOffer = document.querySelector('.offer.is-selected[data-quantity]');
      if (selectedOffer) {
        const offerQty = parseInt(selectedOffer.dataset.quantity) || 1;
        if (offerQty !== this.quantity) {
          this.quantity = offerQty;
        }
      }
    }, 100);
  }

  removeQuantityListeners() {
    document.removeEventListener('offers:quantity-changed', this.handleOffersQuantityChange);

    if (this.mainQuantityInput) {
      this.mainQuantityInput.removeEventListener('change', this.handleMainFormQuantityChange);
    }
  }

  handleOffersQuantityChange(event) {
    const { quantity } = event.detail;
    if (quantity && quantity !== this.quantity) {
      this.quantity = quantity;
    }
  }

  handleMainFormQuantityChange() {
    const qty = parseInt(this.mainQuantityInput.value) || 1;
    if (qty !== this.quantity) {
      this.quantity = qty;
    }
  }

  initializeSplide() {
    // Only initialize on mobile
    if (window.innerWidth >= 750) return;

    const splideEl = this.mobileMenu?.querySelector('[data-splide-nav]');
    if (!splideEl) return;

    // Wait for Splide to load
    if (typeof Splide === 'undefined') {
      setTimeout(() => this.initializeSplide(), 100);
      return;
    }

    this.splideInstance = new Splide(splideEl, {
      type: 'slide',
      rewind: false,
      drag: 'free',
      snap: true,
      perMove: 1,
      autoWidth: true,
      gap: this.config.mobileMenuGap + 'px',
      padding: { left: '0px', right: '0px' },
      arrows: false,
      pagination: false,
      focus: 0,
      trimSpace: true,
      speed: 300,
      i18n: {
        prev: 'Previous item',
        next: 'Next item',
      }
    });

    this.splideInstance.mount();
  }

  initializeVisibility() {
    // Docked mode
    if (this.config.docked) {
      this.isVisible = true;
      this.setupDockedMode();
      return;
    }

    if (this.config.visibility === 'always') {
      requestAnimationFrame(() => this.show());
    } else {
      // Check initial scroll position
      this.checkScrollVisibility();
    }
  }

  setupDockedMode() {
    this.isStuck = false;
    this.isDocked = false;
    this.dockedScrollTicking = false;

    // Create placeholder to prevent content jump
    this.dockedPlaceholder = document.createElement('div');
    this.dockedPlaceholder.style.margin = '0';
    this.dockedPlaceholder.style.padding = '0';
    this.dockedPlaceholder.style.border = 'none';
    this.dockedPlaceholder.style.visibility = 'hidden';
    this.parentNode.insertBefore(this.dockedPlaceholder, this.nextSibling);

    if (this.config.position === 'top') {
      // Top mode: starts in document flow, sticks when scrolled past
      // Cache position and height after a brief delay to ensure page has settled
      requestAnimationFrame(() => {
        const rect = this.getBoundingClientRect();
        // Use getBoundingClientRect for accurate height including any layout effects
        this.dockedHeight = Math.round(rect.height);
        this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
        this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
        this.dockedPlaceholder.style.display = 'none';
        // Now safe to start listening
        this.startDockedListeners();
      });
    } else {
      // Cache element height for bottom mode
      this.dockedHeight = this.offsetHeight;
      // Bottom mode: starts fixed, docks when scrolled to
      this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
      this.dockedPlaceholder.style.display = 'block';
      this.startDockedListeners();
    }
  }

  startDockedListeners() {
    // Bind handlers
    this.onDockedScroll = this.handleDockedScroll.bind(this);
    this.onDockedResize = this.handleDockedResize.bind(this);

    window.addEventListener('scroll', this.onDockedScroll, { passive: true });
    window.addEventListener('resize', this.onDockedResize, { passive: true });

    // Initial check
    this.handleDockedScroll();
  }

  handleDockedResize() {
    // Recalculate position and height on resize
    const rect = this.getBoundingClientRect();
    if (this.config.position === 'top' && !this.isStuck) {
      this.dockedOriginalTop = Math.round(rect.top + window.scrollY);
      this.dockedHeight = Math.round(rect.height);
    } else {
      this.dockedHeight = this.offsetHeight;
    }
    this.dockedPlaceholder.style.height = this.dockedHeight + 'px';
  }

  handleDockedScroll() {
    if (!this.config.docked) return;

    // Throttle with requestAnimationFrame to prevent excessive calculations
    if (!this.dockedScrollTicking) {
      this.dockedScrollTicking = true;
      requestAnimationFrame(() => {
        if (this.config.position === 'top') {
          this.handleTopDockedScroll();
        } else {
          this.handleBottomDockedScroll();
        }
        this.dockedScrollTicking = false;
      });
    }
  }

  handleTopDockedScroll() {
    // Round scroll position to avoid sub-pixel jitter
    const scrollY = Math.round(window.scrollY);

    // Get the current header offset - this is where the sticky section will be positioned
    // When header is hidden: offset = 0, when header is visible/sticky: offset = header height
    const headerOffset = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-offset')) || 0;

    // Calculate the stick threshold accounting for header offset
    // The section should stick when its viewport position reaches the header offset
    // Section viewport top = dockedOriginalTop - scrollY
    // Stick when: dockedOriginalTop - scrollY <= headerOffset
    // Which means: scrollY >= dockedOriginalTop - headerOffset
    const stickThreshold = this.dockedOriginalTop - headerOffset;

    // Hysteresis buffer: require scrolling a bit past threshold to unstick
    // This prevents rapid toggling when at the boundary
    const unstickBuffer = 5;

    if (!this.isStuck) {
      // Check if we should stick
      if (scrollY >= stickThreshold) {
        this.isStuck = true;
        this.dockedPlaceholder.style.display = 'block';
        this.classList.add('is-stuck');
      }
    } else {
      // Already stuck - check if we should unstick
      // Recalculate threshold in case header offset changed while stuck
      const currentStickThreshold = this.dockedOriginalTop - headerOffset;
      if (scrollY < currentStickThreshold - unstickBuffer) {
        this.isStuck = false;
        this.dockedPlaceholder.style.display = 'none';
        this.classList.remove('is-stuck');
      }
    }
  }

  handleBottomDockedScroll() {
    const viewportHeight = window.innerHeight;
    const buffer = 5;

    if (!this.isDocked) {
      const barVisualTop = viewportHeight - this.dockedHeight;
      const placeholderRect = this.dockedPlaceholder.getBoundingClientRect();

      if (placeholderRect.top <= barVisualTop) {
        this.dockScrollThreshold = window.scrollY;
        this.isDocked = true;
        this.dockedPlaceholder.style.display = 'none';
        this.classList.add('is-docked');
      }
    } else {
      if (window.scrollY < this.dockScrollThreshold - buffer) {
        this.isDocked = false;
        this.dockedPlaceholder.style.display = 'block';
        this.classList.remove('is-docked');
      }
    }
  }

  // === SECTION OBSERVERS ===

  setupSectionObservers() {
    // Find all unique anchor IDs from nav items and store section references
    this.anchorSections = [];
    const seenAnchors = new Set();

    this.navItems.forEach(item => {
      const anchorId = item.dataset.anchorId;
      if (anchorId && !seenAnchors.has(anchorId)) {
        seenAnchors.add(anchorId);
        const section = document.getElementById(anchorId);
        if (section) {
          this.anchorSections.push({
            id: anchorId,
            element: section,
            navIndex: parseInt(item.dataset.index),
            cachedTop: 0 // Will be calculated
          });
        }
      }
    });

    // Cache section positions after layout settles
    // Use multiple frames to ensure layout is complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.cacheSectionPositions();
        this.updateActiveSection();
      });
    });

    // Set up scroll listener with requestAnimationFrame throttling
    this.activeScrollTicking = false;
    this.onActiveScroll = () => {
      if (!this.activeScrollTicking) {
        this.activeScrollTicking = true;
        requestAnimationFrame(() => {
          this.updateActiveSection();
          this.activeScrollTicking = false;
        });
      }
    };
    window.addEventListener('scroll', this.onActiveScroll, { passive: true });
  }

  cacheSectionPositions() {
    // Calculate and cache absolute document positions for each section
    const scrollY = window.scrollY;

    this.anchorSections.forEach(section => {
      const rect = section.element.getBoundingClientRect();
      section.cachedTop = rect.top + scrollY;
    });

    // Sort sections by their document position (top to bottom)
    this.anchorSections.sort((a, b) => a.cachedTop - b.cachedTop);
  }

  updateActiveSection() {
    if (this.isScrolling || this.anchorSections.length === 0) return;

    const scrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    // Activation threshold: 25% down from top of viewport
    const threshold = scrollY + (viewportHeight * 0.25);

    let activeSection = null;

    // Simple algorithm: find the LAST section whose top is at or above the threshold
    for (const section of this.anchorSections) {
      if (section.cachedTop <= threshold) {
        activeSection = section;
      }
    }

    // Check if we're above ALL sections
    if (!activeSection) {
      this.clearActiveItem();
      return;
    }

    // Check if the active section's bottom has scrolled above the viewport
    // meaning the section is no longer visible at all
    const sectionBottom = activeSection.cachedTop + activeSection.element.offsetHeight;

    // Find the next section after the active one
    const activeIdx = this.anchorSections.indexOf(activeSection);
    const nextSection = this.anchorSections[activeIdx + 1] || null;

    // If the section bottom is above the viewport top, the section is fully scrolled past.
    // But only deactivate if the next section hasn't reached the threshold yet
    // (if it had, it would be the activeSection instead)
    if (sectionBottom < scrollY && (!nextSection || nextSection.cachedTop > threshold)) {
      this.clearActiveItem();
      return;
    }

    // Only update if the active section actually changed
    if (activeSection.navIndex !== this.activeIndex) {
      this.setActiveItem(activeSection.navIndex);
    }
  }

  destroySectionObservers() {
    if (this.onActiveScroll) {
      window.removeEventListener('scroll', this.onActiveScroll);
    }
    this.anchorSections = [];
  }

  // === ACTIVE STATE MANAGEMENT ===

  setActiveItem(index) {
    if (this.activeIndex === index) return;

    const isFirstActivation = this.activeIndex === -1;
    this.activeIndex = index;

    // Update active classes on all nav items (both desktop and mobile)
    this.navItems.forEach((item, i) => {
      const itemIndex = parseInt(item.dataset.index);
      item.classList.toggle('is-active', itemIndex === index);
    });

    // Fix for initial load: explicitly set active text color via inline style
    // This bypasses CSS cascade timing issues on first activation
    if (this.config.activeStyle === 'background') {
      if (isFirstActivation) {
        // Determine the active text color based on color type
        let activeTextColor = null;
        if (this.config.activeColorType === 'custom' && this.config.activeTextColor) {
          activeTextColor = this.config.activeTextColor;
        } else if (this.config.activeColorType === 'scheme' && this.desktopMenu) {
          // Get the foreground color from the color scheme applied to menu-desktop
          const computedStyle = getComputedStyle(this.desktopMenu);
          activeTextColor = computedStyle.getPropertyValue('--color-foreground').trim();
          if (activeTextColor) {
            activeTextColor = `rgb(${activeTextColor})`;
          }
        }

        // On first activation, set color directly via inline style
        if (activeTextColor) {
          this.navItems.forEach(item => {
            if (item.classList.contains('is-active')) {
              item.style.color = activeTextColor;
            }
          });
        }
      } else {
        // On subsequent activations, clear any inline colors so CSS takes over
        this.navItems.forEach(item => {
          item.style.color = '';
        });
      }
    }

    // Update sliding indicators (desktop only)
    this.updateIndicators();

    // On mobile, scroll Splide to show active item
    if (this.splideInstance && window.innerWidth < 750) {
      // Find the actual Splide slide index that contains the active nav item
      // We need to count ALL slides (including logo/button slides), not just nav items
      const allSlides = this.mobileMenu?.querySelectorAll('.splide__slide');
      if (allSlides) {
        allSlides.forEach((slide, splideIndex) => {
          const navItem = slide.querySelector('[data-nav-item]');
          if (navItem && parseInt(navItem.dataset.index) === index) {
            this.splideInstance.go(splideIndex);
          }
        });
      }
    }
  }

  clearActiveItem() {
    if (this.activeIndex === -1) return;
    this.activeIndex = -1;

    // Remove active class from all items
    this.navItems.forEach(item => {
      item.classList.remove('is-active');
      item.style.color = '';
    });

    // Fade out sliding background
    if (this.slidingBg) {
      this.slidingBg.style.opacity = '0';
    }

    // Fade out underline
    if (this.underline) {
      this.underline.style.opacity = '0';
    }
  }

  updateIndicators() {
    if (this.activeIndex < 0) return;

    // Restore indicator opacity when reactivating
    if (this.slidingBg) {
      this.slidingBg.style.opacity = '';
    }
    if (this.underline) {
      this.underline.style.opacity = '';
    }

    // Find the active item in desktop menu
    const desktopItems = this.desktopMenu?.querySelectorAll('[data-nav-item]');
    if (!desktopItems) return;

    let activeItem = null;
    desktopItems.forEach(item => {
      if (parseInt(item.dataset.index) === this.activeIndex) {
        activeItem = item;
      }
    });

    if (!activeItem || !this.desktopMenu) return;

    const menuRect = this.desktopMenu.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();

    const left = itemRect.left - menuRect.left;
    const width = itemRect.width;

    // Update sliding background (only for slide animation)
    if (this.slidingBg && this.config.backgroundAnimation === 'slide') {
      this.desktopMenu.style.setProperty('--sliding-bg-left', left + 'px');
      this.desktopMenu.style.setProperty('--sliding-bg-width', width + 'px');
    }

    // Update underline
    if (this.underline) {
      let underlineLeft = left;
      let underlineWidth = width;

      // Text-only width
      if (this.config.underlineWidth === 'text') {
        const textSpan = activeItem.querySelector('.sticky-navigation__item-text');
        if (textSpan) {
          const textRect = textSpan.getBoundingClientRect();
          const extraWidth = this.config.underlineExtraWidth;
          // Center the extra width around the text
          underlineLeft = (textRect.left - menuRect.left) - (extraWidth / 2);
          underlineWidth = textRect.width + extraWidth;
        }
      }

      this.desktopMenu.style.setProperty('--underline-left', underlineLeft + 'px');
      this.desktopMenu.style.setProperty('--underline-width', underlineWidth + 'px');
    }
  }

  onResize() {
    // Close drawer on viewport change (e.g. theme editor switching mobile/desktop)
    if (this.drawerOpen) {
      this.closeDrawer();
    }

    // Reinitialize Splide on breakpoint change
    const isMobile = window.innerWidth < 750;

    if (isMobile && !this.splideInstance) {
      this.initializeSplide();
    } else if (!isMobile && this.splideInstance) {
      this.splideInstance.destroy();
      this.splideInstance = null;
    }

    // Recache section positions after resize (layout may have changed)
    if (this.anchorSections.length > 0) {
      this.cacheSectionPositions();
    }

    // Update indicators and active section after resize
    requestAnimationFrame(() => {
      this.updateIndicators();
      this.updateActiveSection();
    });
  }

  // === EVENT HANDLERS ===

  onScroll() {
    // Handle custom visibility (scroll percentage)
    if (this.config.visibility === 'custom' && !this.config.docked) {
      this.checkScrollVisibility();
    }
  }

  checkScrollVisibility() {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = scrollHeight > 0 ? (window.scrollY / scrollHeight) * 100 : 0;

    if (scrollPercent >= this.config.scrollPercentage) {
      this.show();
    } else {
      this.hide();
    }
  }

  onNavItemClick(event) {
    const item = event.currentTarget;
    const anchorId = item.dataset.anchorId;
    const scrollOffset = parseInt(item.dataset.scrollOffset) || 20;
    const index = parseInt(item.dataset.index);

    // Set active immediately for feedback
    this.setActiveItem(index);

    // Close drawer if open
    if (this.drawerOpen) {
      this.closeDrawer();
    }

    // Scroll to section
    if (anchorId) {
      this.scrollToAnchor(anchorId, scrollOffset);
    }
  }

  onButtonClick(event) {
    // For page links, let the browser handle naturally
    if (this.config.buttonFunction === 'page') {
      // Don't prevent default - let the <a> tag work
      return;
    }

    event.preventDefault();

    if (this.config.buttonFunction === 'add_to_cart') {
      this.addToCart();
    } else if (this.config.buttonFunction === 'scroll' || this.config.buttonFunction === 'scroll_to') {
      this.scrollToAnchor(this.config.buttonScrollAnchor, this.config.buttonScrollOffset);
    }
  }

  // === DRAWER ===

  openDrawer() {
    if (!this.drawerPanel || !this.drawerOverlay) return;
    this.drawerOpen = true;
    // Position panel directly below the bar
    var bar = this.querySelector('.sticky-navigation__bar');
    if (bar) {
      var barRect = bar.getBoundingClientRect();
      this.drawerPanel.style.top = barRect.bottom + 'px';
      this.drawerPanel.style.height = 'calc(100vh - ' + barRect.bottom + 'px)';
    }
    this.drawerOverlay.style.display = 'block';
    // Force reflow before adding class for transition
    this.drawerOverlay.offsetHeight;
    this.drawerOverlay.classList.add('is-visible');
    this.drawerPanel.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this.onDrawerEscape);
    // Swap icon to close
    if (this.drawerIconOpen) this.drawerIconOpen.style.display = 'none';
    if (this.drawerIconClose) this.drawerIconClose.style.display = '';
  }

  closeDrawer() {
    if (!this.drawerPanel || !this.drawerOverlay) return;
    this.drawerOpen = false;
    this.drawerPanel.classList.remove('is-open');
    this.drawerOverlay.classList.remove('is-visible');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.onDrawerEscape);
    // Swap icon to menu
    if (this.drawerIconOpen) this.drawerIconOpen.style.display = '';
    if (this.drawerIconClose) this.drawerIconClose.style.display = 'none';
    // Hide overlay after transition
    setTimeout(() => {
      if (!this.drawerOpen) {
        this.drawerOverlay.style.display = 'none';
      }
    }, 300);
  }

  onDrawerEscape(event) {
    if (event.key === 'Escape' && this.drawerOpen) {
      this.closeDrawer();
    }
  }

  // === VISIBILITY ===

  show() {
    if (this.isVisible) return;
    this.isVisible = true;
    this.classList.add('is-visible');
  }

  hide() {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.classList.remove('is-visible');
  }

  // === ACTIONS ===

  scrollToAnchor(anchorId, offset = 20) {
    if (!anchorId) return;

    const target = document.getElementById(anchorId);
    if (target) {
      // Prevent observer from changing active item during scroll
      this.isScrolling = true;

      // Clear any existing scroll end detection
      if (this.scrollEndTimeout) {
        clearTimeout(this.scrollEndTimeout);
      }

      // Calculate offset including header and sticky nav
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      const stickyNavHeight = this.config.position === 'top' ? this.offsetHeight : 0;
      const totalOffset = headerHeight + stickyNavHeight + offset;

      const targetPosition = target.getBoundingClientRect().top + window.scrollY - totalOffset;

      // Store state for scroll end detection
      this.scrollTargetPosition = Math.round(targetPosition);
      this.lastScrollCheckPosition = window.scrollY;
      this.scrollStableCount = 0;

      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });

      // Start detecting when scroll actually ends
      this.detectScrollEnd();
    }
  }

  detectScrollEnd() {
    this.scrollEndTimeout = setTimeout(() => {
      const currentScroll = Math.round(window.scrollY);

      // Check if scroll position has stabilized (hasn't changed since last check)
      // or if we've reached very close to the target
      const hasStabilized = Math.abs(currentScroll - this.lastScrollCheckPosition) < 2;
      const nearTarget = Math.abs(currentScroll - this.scrollTargetPosition) < 10;

      if (hasStabilized || nearTarget) {
        this.scrollStableCount++;

        // Require 3 consecutive stable checks (~150ms of no movement)
        if (this.scrollStableCount >= 3) {
          this.isScrolling = false;
          this.scrollTargetPosition = null;
          return;
        }
      } else {
        // Still scrolling, reset stable count
        this.scrollStableCount = 0;
      }

      this.lastScrollCheckPosition = currentScroll;
      this.detectScrollEnd();
    }, 50);
  }

  async addToCart() {
    let variantId = null;

    // Use custom product if selected, otherwise use current page's product
    if (this.config.buttonCustomVariantId) {
      variantId = this.config.buttonCustomVariantId;
    } else {
      const productForm = document.querySelector('product-form form, form[action*="/cart/add"]');
      if (!productForm) {
        console.warn('Sticky Navigation: No product form found for add to cart');
        return;
      }

      const variantInput = productForm.querySelector('input[name="id"]');
      if (!variantInput || !variantInput.value) {
        console.warn('Sticky Navigation: No variant ID found');
        return;
      }
      variantId = parseInt(variantInput.value);
    }

    // Show loading state on both desktop and mobile buttons
    const buttons = [this.button, this.mobileButton].filter(Boolean);
    buttons.forEach(btn => {
      btn.classList.add('is-loading');
      btn.disabled = true;
    });

    try {
      // Use Section Rendering API so the response includes pre-rendered HTML
      const cartDrawer = document.querySelector('sugar-cart-drawer');
      const sections = cartDrawer?.getSectionsToRender?.().map(s => s.id) || ['sugar-cart-drawer', 'cart-icon-bubble'];

      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: this.quantity }],
          sections,
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) throw new Error('Add to cart failed');

      const data = await response.json();

      if (data.status) {
        console.error('Sticky Navigation: Cart error', data.description);
        return;
      }

      // Skip cart: redirect directly to checkout
      if (this.config.buttonSkipCart) {
        window.location.href = '/checkout';
        return;
      }

      // Publish cart update event
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sticky-navigation',
          productVariantId: variantId,
          cartData: data
        });
      }

      // renderContents handles all DOM updates (header, items, footer, progress, styles)
      // and opens the drawer automatically
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      } else if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } catch (error) {
      console.error('Sticky Navigation: Add to cart error', error);
    } finally {
      buttons.forEach(btn => {
        btn.classList.remove('is-loading');
        btn.disabled = false;
      });
    }
  }
}

// Register the custom element
if (!customElements.get('sticky-navigation')) {
  customElements.define('sticky-navigation', StickyNavigation);
}
